import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { formatRelativeTime, getInitials } from '@/lib/utils';

export interface ActivityItem {
  id: string;
  type: 'stage_change' | 'application_created' | 'candidate_created' | 'job_created' | 'note_added';
  description: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
    avatarUrl: string | null;
  };
  entity?: {
    type: 'candidate' | 'job' | 'application';
    id: string;
    name: string;
  };
  createdAt: string;
}

interface ActivityFeedProps {
  activities: ActivityItem[] | null;
  isLoading: boolean;
}

function getEntityLink(entity: ActivityItem['entity']): string | null {
  if (!entity) return null;
  switch (entity.type) {
    case 'candidate':
      return `/candidates/${entity.id}`;
    case 'job':
      return `/jobs/${entity.id}`;
    case 'application':
      return `/jobs/${entity.id}/pipeline`;
    default:
      return null;
  }
}

export function ActivityFeed({ activities, isLoading }: ActivityFeedProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Recent Activity</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex gap-3">
                <Skeleton className="h-8 w-8 rounded-full shrink-0" />
                <div className="space-y-1.5 flex-1">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-3 w-16" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">Recent Activity</CardTitle>
      </CardHeader>
      <CardContent>
        {!activities || activities.length === 0 ? (
          <div className="h-48 flex items-center justify-center text-text-tertiary text-sm">
            No recent activity
          </div>
        ) : (
          <div className="space-y-4 max-h-80 overflow-y-auto">
            {activities.map((activity) => {
              const entityLink = getEntityLink(activity.entity);

              return (
                <div
                  key={activity.id}
                  className="flex gap-3 rounded-lg px-2 py-1.5 -mx-2 transition-colors hover:bg-white/5"
                >
                  <Avatar className="h-8 w-8 shrink-0">
                    {activity.user.avatarUrl && (
                      <AvatarImage src={activity.user.avatarUrl} />
                    )}
                    <AvatarFallback className="text-xs">
                      {getInitials(activity.user.firstName, activity.user.lastName)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-text-secondary leading-snug">
                      <span className="font-medium text-text-primary">
                        {activity.user.firstName} {activity.user.lastName}
                      </span>{' '}
                      {activity.description}
                      {activity.entity && entityLink && (
                        <>
                          {' '}
                          <Link
                            to={entityLink}
                            className="font-medium text-emerald-500 hover:text-emerald-400 hover:underline"
                          >
                            {activity.entity.name}
                          </Link>
                        </>
                      )}
                    </p>
                    <p className="text-xs text-text-tertiary mt-0.5">
                      {formatRelativeTime(activity.createdAt)}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
